/**
 * Interaction — zod schemas for runtime validation
 *
 * Mirrors the types in ../types/interaction.ts. Used at every socket boundary
 * so a malformed payload fails loud at the receiver, not silently deep inside
 * a handler. See docs/superpowers/specs/2026-04-20-input-station.md.
 */

import { z } from 'zod';
import { QuestionSchema } from './common.schema.js';

// ──────────────────────────────────────────────────────────────────────────────
// Kinds
// ──────────────────────────────────────────────────────────────────────────────

// Deliberately NARROWER than the InteractionKind TS union: 'hands_approval' is
// omitted. This schema's only runtime consumer is the sandbox-facing register
// route (/api/internal/interactions RegisterBody): 'hands_approval' is minted
// exclusively by the SERVER-SIDE hands gate, and a sandbox must not be able to
// register a decoy approval row (computer-use S3 hardening).
export const InteractionKindSchema = z.enum([
  'ask_question',
  'composio_auth',
  'tool_permission',
  'credit_approval',
  'exit_plan',
  'custom',
]);

// ──────────────────────────────────────────────────────────────────────────────
// Permission suggestion (SDK 0.2.49)
// ──────────────────────────────────────────────────────────────────────────────

export const PermissionSuggestionSchema = z.object({
  type: z.literal('addRules'),
  rules: z.array(
    z.object({
      toolName: z.string(),
      ruleContent: z.string().optional(),
    })
  ),
  behavior: z.enum(['allow', 'deny']),
  destination: z.enum(['session', 'project', 'user']),
});

// ──────────────────────────────────────────────────────────────────────────────
// Per-kind request payloads
// ──────────────────────────────────────────────────────────────────────────────

const BaseRequestShape = {
  id: z.string().min(1),
  taskId: z.string().min(1),
  createdAt: z.number().int().nonnegative(),
  toolUseId: z.string().optional(),
};

export const AskQuestionRequestSchema = z.object({
  ...BaseRequestShape,
  kind: z.literal('ask_question'),
  questions: z.array(QuestionSchema).min(1).max(4),
  // Done rail + browser handoff fields — all OPTIONAL and additive. MUST be
  // declared here: z.object STRIPS unknown keys, so without them any parser
  // using this schema would silently drop the handoff card's surface / page.
  // Absent on legacy payloads → the object is byte-identical to today.
  intent: z.enum(['handoff', 'question']).optional(),
  source: z.enum(['request_user_input', 'done_verdict']).optional(),
  surface: z.enum(['browser', 'phone']).optional(),
  url: z.string().url().optional(),
  frame: z.string().max(120_000).optional(),
  why: z.string().max(300).optional(),
  // File answers (2026-09-24): how the person answers. Absent = text.
  answer: z.enum(['text', 'file']).optional(),
});

export const ComposioAuthRequestSchema = z.object({
  ...BaseRequestShape,
  kind: z.literal('composio_auth'),
  toolkit: z.string().min(1),
  displayName: z.string().min(1),
  authUrl: z.string().url().optional(),
  triggeringToolName: z.string().optional(),
  /**
   * Multi-account (design §7). `accountHint` names WHICH account the agent needs
   * (connected-account id or alias); `mode: 'add'` marks an ask that deliberately
   * wants a NEW account even though a sibling is ACTIVE. Both are optional and
   * absent asks behave exactly as before — the runner starts writing them in PR 2,
   * but the schema must carry them now or z.object would strip them in transit.
   */
  accountHint: z.string().min(1).optional(),
  mode: z.literal('add').optional(),
});

// Rich-card detail row (describePermissionAsk). `tone` styles the value.
export const ToolPermissionDetailSchema = z.object({
  label: z.string(),
  value: z.string(),
  tone: z.enum(['danger', 'quote']).optional(),
});

// Why the ask fired — family + floor + why-line (describePermissionAsk).
export const ToolPermissionReasonSchema = z.object({
  family: z.enum([
    'readonly',
    'workspace_write',
    'external_send',
    'external_mutation',
    'dangerous_delete',
    'unknown',
    // A force push, a remote branch delete (agent-sdk git-command-catalog): always asks.
    'protected_remote',
    'payment',
  ]),
  floor: z.enum(['hard', 'soft', 'rule']),
  line: z.string(),
});

export const ToolPermissionRequestSchema = z.object({
  ...BaseRequestShape,
  kind: z.literal('tool_permission'),
  toolName: z.string().min(1),
  // Tool input is tool-specific — we validate shape, not content.
  toolInput: z.record(z.string(), z.unknown()),
  // Plain-language one-liner computed at request assembly (describePermissionAsk).
  summary: z.string().optional(),
  suggestions: z.array(PermissionSuggestionSchema).optional(),
  // Rich permission-card fields (describePermissionAsk) — all OPTIONAL and
  // additive. MUST be declared here: z.object STRIPS unknown keys, so without
  // them the sandbox-facing register route would silently drop the enriched
  // fields and the durable interaction row (and its cross-tab / reload render)
  // would lose the rich card. Absent on legacy payloads → the object is
  // byte-identical to today.
  details: z.array(ToolPermissionDetailSchema).optional(),
  actionLabel: z.string().optional(),
  reason: ToolPermissionReasonSchema.optional(),
  // Platform API v1: the call is one of the company's own tools, waiting for the customer's yes (agent-sdk
  // company-action.ts). Declared for the same reason as the card fields above: the register route strips what is not.
  company: z.object({ tool: z.string().min(1), server: z.string().min(1).optional(), headline: z.string().min(1).max(200).optional() }).optional(),
  // APPROVALS_GATE_V1: held by the version's approvals.always (agent-sdk approval-hold.ts). Declared so the register route
  // keeps it: /v1's present.ts reads it to require present_user, and a stripped mark would let a company answer alone.
  approval: z.object({
    classes: z.array(z.enum(['payment', 'booking', 'cancellation', 'send_email', 'publish', 'delete'])),
    unknown: z.literal(true).optional(),
  }).optional(),
});

export const CreditApprovalRequestSchema = z.object({
  ...BaseRequestShape,
  kind: z.literal('credit_approval'),
  toolName: z.string().min(1),
  estimatedCostUsd: z.number().nonnegative(),
  balanceUsd: z.number(),
});

export const ExitPlanRequestSchema = z.object({
  ...BaseRequestShape,
  kind: z.literal('exit_plan'),
  plan: z.string(),
  planFilePath: z.string().optional(),
});

export const CustomInteractionRequestSchema = z.object({
  ...BaseRequestShape,
  kind: z.literal('custom'),
  renderer: z.string().min(1),
  title: z.string().optional(),
  description: z.string().optional(),
  config: z.record(z.string(), z.unknown()),
  submitLabel: z.string().optional(),
  allowSkip: z.boolean().optional(),
  skipLabel: z.string().optional(),
});

// GUARD (computer-use S3): 'hands_approval' requests are deliberately NOT in
// this union. hands_approval is minted server-side by the hands gate and
// rendered via a cast on the OUTBOUND path only — never parsed inbound (the
// sandbox-facing register route must keep rejecting the kind). Consequence: a
// future consumer that parses INTERACTION_REQUIRED payloads through this schema
// (or InteractionRequiredPayloadSchema below) would silently DROP hands_approval
// requests as parse failures — and the held-approval prompt would never
// surface. Parse selectively by kind, or widen this union consciously (and
// re-audit the register route first).
export const InteractionRequestSchema = z.discriminatedUnion('kind', [
  AskQuestionRequestSchema,
  ComposioAuthRequestSchema,
  ToolPermissionRequestSchema,
  CreditApprovalRequestSchema,
  ExitPlanRequestSchema,
  CustomInteractionRequestSchema,
]);

// ──────────────────────────────────────────────────────────────────────────────
// Response
// ──────────────────────────────────────────────────────────────────────────────

export const InteractionDenyReasonSchema = z.enum([
  'user_redirected',
  'user_declined',
  'user_skipped',
  'aborted',
  'timed_out',
  'awaiting_user_input',
]);

export const InteractionAllowSchema = z.object({
  behavior: z.literal('allow'),
  updatedInput: z.unknown().optional(),
  addRule: PermissionSuggestionSchema.optional(),
  // Computer-use S3 — the hands_approval nonce echoed by the resolving client.
  // Must be in the schema: z.object STRIPS unknown keys, and the resolve route
  // parses the outcome through this schema — without this field the echo would
  // be silently dropped and the gate would treat every approval as declined.
  nonce: z.string().optional(),
});

export const InteractionDenySchema = z.object({
  behavior: z.literal('deny'),
  reason: InteractionDenyReasonSchema,
  message: z.string().optional(),
});

export const InteractionOutcomeSchema = z.discriminatedUnion('behavior', [
  InteractionAllowSchema,
  InteractionDenySchema,
]);

export const InteractionResponseSchema = z.object({
  taskId: z.string().min(1),
  interactionId: z.string().min(1),
  outcome: InteractionOutcomeSchema,
});

// ──────────────────────────────────────────────────────────────────────────────
// Envelope events
// ──────────────────────────────────────────────────────────────────────────────

export const InteractionRequiredPayloadSchema = z.object({
  taskId: z.string().min(1),
  request: InteractionRequestSchema,
});

/**
 * interaction:required — THE WIRE ENVELOPE, the schema the event registry (and so
 * the relay's forward parse gate) carries. Emitters: apps/sandbox/src/sdk/
 * interaction-gateway.ts:260 (+ triggeredBy / expiresAtMs, subagentId /
 * subagentPath for a child's ask), apps/sandbox/src/stream/harness-sink.ts:468,
 * and the server's own gates (hands-approval-register.service.ts:35,
 * job-credits.service.ts:155) which send { taskId, request } only.
 *
 * `request` is validated by its discriminator ONLY, deliberately: the GUARD
 * above says why — 'hands_approval' is minted server-side and is not in
 * InteractionRequestSchema's union, so parsing the kind union here would drop
 * every held-approval prompt at the gate. Parse the kind selectively downstream
 * (InteractionRequiredPayloadSchema stays for that); the envelope stays loose.
 */
export const InteractionRequiredEnvelopeSchema = z.object({
  taskId: z.string().min(1),
  request: z.object({ id: z.string().min(1), kind: z.string().min(1) }).passthrough(),
  triggeredBy: z.string().optional(),
  expiresAtMs: z.number().optional(),
  subagentId: z.string().optional(),
  subagentPath: z.array(z.record(z.string(), z.unknown())).optional(),
});

export const InteractionResponsePayloadSchema = z.object({
  taskId: z.string().min(1),
  interactionId: z.string().min(1),
  outcome: InteractionOutcomeSchema,
});
